import { Sort } from '@angular/material/sort';
import * as lodash from 'lodash';
import { ParserHlp } from 'src/lib/fliko/helpers/parser.helper';
import { SortCriteria, SortOrder } from '../models/sort-criteria';

import { environment } from '../../../environments/environment';

enum CharType {
  DIGIT,
  LETTER,
  SEPARATOR
}

export class SortHelper {
  /**
   * Generates comparator function using given criteria, applied in order
   * @param {(SortCriteria | undefined)[]} sortCriteria, undefined means {order: ASC}
   * @return {function(a,b): number}
   */
  public static getComp<T>(...sortCriteria: (SortCriteria | undefined)[]): (a: T, b: T) => number {
    return (a: any, b: any) => {
      let result = 0;
      for (let i = 0; i < sortCriteria.length && result == 0; i++) {
        const criterium = sortCriteria[i];

        let aParam = a;
        let bParam = b;

        if (criterium?.param) {
          if (a) aParam = lodash.get(a, criterium.param);
          if (b) bParam = lodash.get(b, criterium.param);
        }

        if (criterium?.transform) {
          aParam = criterium.transform(aParam);
          bParam = criterium.transform(bParam);
        }

        if (bParam == null) {
          result = aParam != null ? 1 : 0;
          continue;
        }
        if (aParam == null) {
          result = -1;
          continue;
        }

        if (criterium?.separator) {
          const aSep: string[] = aParam.toString().split(criterium.separator);
          const bSep: string[] = bParam.toString().split(criterium.separator);

          if (aSep.length != bSep.length) {
            result = aSep.length - bSep.length;
            continue;
          }

          for (let i = 0; i < aSep.length; ++i) {
            const numA = this.stringToNumber(aSep[i]);
            const numB = this.stringToNumber(bSep[i]);
            const res = numA && numB ? numA - numB : aSep[i].localeCompare(bSep[i], environment.language);
            if (res != 0) {
              result = criterium?.order == SortOrder.DSC ? -res : res;
              break;
            }
          }
          continue;
        }

        // prettier-ignore
        const r = typeof aParam == 'number' && typeof bParam == 'number'
          ? aParam - bParam
          : aParam.toString().localeCompare(bParam.toString(), environment.language);

        result = criterium?.order == SortOrder.DSC ? -r : r;
      }
      return result;
    };
  }

  public static numeralStringSorter(forClickableRowTable: boolean): (data: any[], sortOption: Sort) => any[] {
    return (data: any[], sortOption: Sort) => {
      if (!sortOption.active || !sortOption.direction) return data;
      const get = forClickableRowTable ? (x: any) => x[sortOption.active].value : (x: any) => x[sortOption.active];
      data = data.map((x) => {
        return { ...x, sortKey: SortHelper.numeralStringParser(get(x)) };
      });
      const collator = new Intl.Collator('pl');
      return data.sort((x, y) =>
        sortOption.direction == 'asc'
          ? SortHelper.numeralStringComparator(x.sortKey, y.sortKey, collator.compare)
          : SortHelper.numeralStringComparator(y.sortKey, x.sortKey, collator.compare)
      );
    };
  }

  public static numeralStringComparator(x: any[], y: any[], strCompare: (a: string, b: string) => number): number {
    if (!x.length) return y.length ? 1 : 0;
    if (!y.length) return -1; // empty on back

    for (let i = 0; i < x.length && i < y.length; i++) {
      if (typeof x[i] == 'number') {
        if (typeof y[i] == 'number') {
          const diff = x[i] - y[i];
          if (diff) return diff;
        } else {
          return 1; // numbers before strings
        }
      } else {
        // ? x[i] is string
        if (typeof y[i] == 'string') {
          const diff = strCompare(x[i], y[i]);
          if (diff) return diff;
        } else {
          return -1; // numbers before strings
        }
      }
    }

    return x.length - y.length; // compare length
  }

  public static numeralStringParser(v: any): (string | number)[] {
    const x = v?.toString() ?? '';
    const arr: (string | number)[] = [];
    let start = 0;
    let lastType = CharType.SEPARATOR;
    let leadingZero = false;
    for (let i = 0; i < x.length; i++) {
      const chType = SortHelper.checkChar(x, i);
      // ? --- [ is digit ] ---------------------------------------------------
      if (chType == CharType.DIGIT) {
        if (lastType == CharType.DIGIT) continue;
        // ? save string
        if (lastType == CharType.LETTER) arr.push(x.substring(start, i));
        // ? was letter or separator
        lastType = CharType.DIGIT;
        if (!leadingZero && x.charCodeAt(i) == 48) leadingZero = true;
        start = i;
      }
      // ? --- [ is letter ] --------------------------------------------------
      else if (chType == CharType.LETTER) {
        if (lastType == CharType.LETTER) continue;
        // ? save number
        if (lastType == CharType.DIGIT) {
          if (leadingZero) {
            arr.push(x.substring(start, i));
            leadingZero = false;
          }
          arr.push(parseInt(x.substring(start, i)));
        }
        // ? was number or separator
        lastType = CharType.LETTER;
        start = i;
      }
      // ? --- [ is separator ] -----------------------------------------------
      else {
        if (lastType == CharType.DIGIT) {
          if (leadingZero) {
            arr.push(x.substring(start, i));
            leadingZero = false;
          }
          arr.push(parseInt(x.substring(start, i)));
        }
        if (lastType == CharType.LETTER) arr.push(x.substring(start, i));
        lastType = CharType.SEPARATOR;
      }
    }

    if (lastType == CharType.DIGIT) {
      if (leadingZero) arr.push(x.substring(start));
      arr.push(parseInt(x.substring(start)));
    }
    if (lastType == CharType.LETTER) {
      arr.push(x.substring(start));
    }

    return arr;
  }

  private static checkChar(x: string, index: number): CharType {
    const code = x.charCodeAt(index);
    if (code >= 48 && code <= 57) return CharType.DIGIT;
    else if ((code >= 65 && code <= 90) || (code >= 97 && code <= 122)) return CharType.LETTER;
    return CharType.SEPARATOR;
  }

  /**
   *  Parse string to number with checking if whole string is number
   * @param {string} str
   * @return {number | null} parsed number or null if string is not all digits
   */
  private static stringToNumber(str: string): number | null {
    for (let i = 0; i < str.length; ++i) {
      if (this.checkChar(str, i) != CharType.DIGIT) return null;
    }
    return ParserHlp.parseFloat(str);
  }
}
